import { useState } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Scan, Search, Shield, Zap, Brain, Activity,
  Hash, Timer, Globe, FileSearch, AlertTriangle,
  CheckCircle2, XCircle, Clock, Target
} from "lucide-react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// JavaScript scanning interface for security analysis
export function JavaScriptScanner() {
  const [scanUrl, setScanUrl] = useState("");
  const [scanType, setScanType] = useState("comprehensive");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const scanMutation = useMutation({
    mutationFn: async (data: { url: string; type: string }) => {
      return apiRequest("/api/scans", "POST", { ...data, userId: "user-1" });
    },
    onSuccess: () => {
      toast({
        title: "Scan initiated successfully",
        description: "JavaScript integrity analysis in progress",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scans"] });
      setScanUrl("");
    },
    onError: (error) => {
      toast({
        title: "Scan failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleScan = () => {
    if (!scanUrl.trim()) return;
    scanMutation.mutate({ url: scanUrl, type: scanType });
  };

  // Fetch real-time compliance data
  const { data: complianceData } = useQuery({
    queryKey: ["/api/compliance/real-time"],
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const scanFeatures = [
    {
      icon: Hash,
      title: "Content Fingerprinting",
      description: "SHA-256 hash validation for all scripts",
      active: true
    },
    {
      icon: Brain,
      title: "Entropy Analysis",
      description: "AI-powered obfuscation detection",
      active: true
    },
    {
      icon: Activity,
      title: "Behavioral Monitoring",
      description: "Dynamic execution pattern analysis",
      active: true
    },
    {
      icon: Globe,
      title: "Domain Surveillance",
      description: "Third-party dependency tracking",
      active: true
    }
  ];

  // Generate compliance checks from authentic API data
  const complianceChecks = complianceData && complianceData.securityHeaders ? [
    { 
      label: "CSP Headers", 
      status: complianceData.securityHeaders.csp.present ? "verified" : "warning", 
      score: complianceData.securityHeaders.csp.score || 0
    },
    { 
      label: "Script Integrity", 
      status: "monitoring", 
      score: Math.round((complianceData.javascriptIntegrity.totalScripts - complianceData.javascriptIntegrity.highEntropyScripts) / complianceData.javascriptIntegrity.totalScripts * 100)
    },
    { 
      label: "HTTPS Enforcement", 
      status: complianceData.networkCompliance.httpsRatio > 80 ? "verified" : "warning", 
      score: Math.round(complianceData.networkCompliance.httpsRatio)
    },
    { 
      label: "Third-party Validation", 
      status: complianceData.networkCompliance.suspiciousDomains === 0 ? "verified" : "warning", 
      score: Math.max(0, 100 - (complianceData.networkCompliance.thirdPartyDomains * 5))
    }
  ] : [
    // Fallback for when no data is available
    { label: "CSP Headers", status: "monitoring", score: 0 },
    { label: "Script Integrity", status: "monitoring", score: 0 },
    { label: "HTTPS Enforcement", status: "monitoring", score: 0 },
    { label: "Third-party Validation", status: "monitoring", score: 0 }
  ];

  return (
    <div className="space-y-6">
      {/* JavaScript Scanning Interface */}
      <Card className="border-emerald-200 dark:border-emerald-800">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-teal-50 dark:from-purple-900/20 dark:to-teal-900/20">
          <CardTitle className="flex items-center gap-2">
            <Scan className="h-5 w-5 text-purple-600" />
            JavaScript Security Scanner
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <Input
                type="url"
                placeholder="Enter website URL (e.g., https://example.com)"
                value={scanUrl}
                onChange={(e) => setScanUrl(e.target.value)}
                className="text-lg"
              />
            </div>
            <select 
              value={scanType}
              onChange={(e) => setScanType(e.target.value)}
              className="px-4 py-2 border rounded-md bg-white dark:bg-slate-800"
            >
              <option value="comprehensive">Comprehensive Analysis</option>
              <option value="quick">Quick Integrity Check</option>
              <option value="deep">Deep Security Audit</option>
            </select>
            <Button 
              onClick={handleScan}
              disabled={!scanUrl.trim() || scanMutation.isPending}
              className="bg-purple-600 hover:bg-emerald-700 px-8"
            >
              {scanMutation.isPending ? (
                <>
                  <Timer className="w-4 h-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Launch Scan
                </>
              )}
            </Button>
          </div>

          {/* Scanning Features Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {scanFeatures.map((feature, index) => (
              <div key={index} className="p-4 rounded-lg border bg-gradient-to-br from-white to-purple-50 dark:from-slate-800 dark:to-purple-900/10">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <feature.icon className="h-4 w-4 text-purple-600" />
                  </div>
                  <Badge variant={feature.active ? "default" : "secondary"} className="text-xs">
                    {feature.active ? "Active" : "Standby"}
                  </Badge>
                </div>
                <div className="text-sm font-medium mb-1">{feature.title}</div>
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  {feature.description}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Real-time Compliance Dashboard */}
      <Card>
        <CardHeader>
          <Link href="/compliance">
            <CardTitle className="flex items-center gap-2 cursor-pointer hover:text-teal-700 transition-colors">
              <Target className="h-5 w-5 text-purple-600" />
              Real-time Compliance Monitoring
            </CardTitle>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {complianceChecks.map((check, index) => (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <Link href="/compliance">
                    <div className="p-4 rounded-lg border hover:border-teal-300 hover:bg-teal-50/50 dark:hover:bg-teal-900/10 transition-all cursor-pointer">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">{check.label}</div>
                        {check.status === 'verified' && <CheckCircle2 className="h-4 w-4 text-purple-500" />}
                        {check.status === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                        {check.status === 'monitoring' && <Clock className="h-4 w-4 text-blue-500" />}
                      </div>
                      <div className="text-2xl font-bold text-purple-600 mb-1">
                        {check.score}%
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${check.score}%` }}
                        ></div>
                      </div>
                    </div>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-sm max-w-xs">
                    <div className="font-medium mb-1">{check.label}</div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {check.status === 'verified' && 'Security compliance verified and actively monitored'}
                      {check.status === 'warning' && 'Security issues detected - immediate attention required'}
                      {check.status === 'monitoring' && 'Continuous monitoring active - no issues detected'}
                    </div>
                    <div className="text-xs">
                      <span className="font-medium">Score:</span> {check.score}% compliance
                      <br />
                      <span className="font-medium">Status:</span> {check.status}
                      <br />
                      <span className="font-medium">Last check:</span> Real-time monitoring
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// JavaScript scan results visualization
export function JavaScriptScanResults() {
  // Fetch real compliance and dashboard data for authentic metrics
  const { data: complianceData } = useQuery({ queryKey: ["/api/compliance/real-time"] });
  const { data: stats } = useQuery({ queryKey: ["/api/dashboard/stats"] });

  const scriptsMonitored = complianceData?.javascriptIntegrity?.totalScripts || 0;
  const externalDomains = complianceData?.networkCompliance?.thirdPartyDomains || 0;
  const integrityViolations = complianceData?.alerts?.critical || 0;
  const statusMessage = integrityViolations === 0 ? 
    "All monitored scripts validated successfully" : 
    `${integrityViolations} critical integrity issue${integrityViolations === 1 ? '' : 's'} detected`;
  const statusBadge = integrityViolations === 0 ? "Verified" : "Alert";

  return (
    <Card>
      <CardHeader>
        <Link href="/scripts">
          <CardTitle className="flex items-center gap-2 cursor-pointer hover:text-cyan-700 transition-colors">
            <FileSearch className="h-5 w-5 text-purple-600" />
            JavaScript Integrity Analysis Results
          </CardTitle>
        </Link>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="h-5 w-5 text-purple-600" />
              <div>
                <div className="font-medium">Script Integrity Status</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {statusMessage}
                </div>
              </div>
              <Badge className={`ml-auto ${integrityViolations === 0 ? 'bg-purple-600' : 'bg-red-600'}`}>
                {statusBadge}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <Link href="/scripts">
                <div className="cursor-pointer hover:bg-cyan-100 dark:hover:bg-cyan-900/30 rounded-lg p-2 transition-colors">
                  <div className="text-2xl font-bold text-purple-600">{scriptsMonitored}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">Scripts Monitored</div>
                </div>
              </Link>
              <Link href="/domains">
                <div className="cursor-pointer hover:bg-emerald-100 dark:hover:bg-purple-900/30 rounded-lg p-2 transition-colors">
                  <div className="text-2xl font-bold text-purple-600">{externalDomains}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">External Domains</div>
                </div>
              </Link>
              <Link href="/scripts">
                <div className="cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg p-2 transition-colors">
                  <div className="text-2xl font-bold text-purple-600">{integrityViolations}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">Integrity Violations</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}




